import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import {
  Anchor,
  Button,
  CountryCodeSelect,
  Flex,
  Input,
  Stack,
  Text,
  TextInput,
  desktopFirstMediaQuery,
} from '@inflearn/ds-react';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { Controller, type SubmitHandler, useForm, useWatch } from 'react-hook-form';

import { useLang } from '@inflearn/i18n-tools';
import Link from 'next/link';
import { NEXT_PUBLIC_CDN_URL } from '../../../../../../appConfig';
import { AUTH_URL } from '../../../../../constants/urls';
import { eventNames } from '../../../../../utils/GTM/eventNames';
import { useTrackMixpanelEvent } from '../../../../../utils/GTM/useTrackMixpanelEvent';
import { getCountryCode } from '../../../../../utils/i18n/getCountryCode';
import MarketingPartnersValidator from '../../../../../validators/MarketingPartnersValidator';
import { CheckboxCard } from '../../../../common/CheckboxCard';
import { useOpenPartnersTermsAlert } from '../terms/useOpenPartnersTermsAlert';
import { RadioCardWithoutIndicator } from './RadioCardWithoutIndicator';
import { PARTNERS_TYPE } from './constants';
import type { Partners } from './types';

interface PartnersSignupFormProps {
  onSubmit: (partners: Partners) => void;
  // 폼의 로딩 상태
  loading?: boolean;
}

export const PartnersSignupForm = ({ onSubmit, loading = false }: PartnersSignupFormProps) => {
  const { t } = useTranslation();
  const { trackMixpanelEvent } = useTrackMixpanelEvent();
  const [isAgreePartnersTerms, setIsAgreePartnersTerms] = useState(false);
  const { lang } = useLang();

  const { openPartnersTermsAlert } = useOpenPartnersTermsAlert({
    onConfirm: () => {
      setIsAgreePartnersTerms(true);
      trackMixpanelEvent(eventNames.CLICK_PARTNERS_TERMS_FINAL_AGREE);
    },
  });

  const form = useForm<Partners>({
    mode: 'onChange',
    resolver: classValidatorResolver(MarketingPartnersValidator),
    defaultValues: {
      type: PARTNERS_TYPE.PERSONAL,
      name: '',
      email: '',
      countryCode: getCountryCode({ lang }),
      phone: '',
      activityLink: '',
    },
  });
  const partnersType = useWatch({ control: form.control, name: 'type' });

  const handleSubmit: SubmitHandler<Partners> = (partners) => {
    trackMixpanelEvent(eventNames.CLICK_PARTNERS_JOIN_BUTTON, {
      marketing_partner_type: partners.type.toLowerCase() as 'business' | 'personal',
      name: partners.name,
      email: partners.email,
      country_code: `+${partners.countryCode}`,
      phone_number: partners.phone,
    });
    onSubmit(partners);
  };

  const handleClickAgreePartnersTerms = () => {
    trackMixpanelEvent(eventNames.CLICK_PARTNERS_TERMS_AGREE);

    if (!isAgreePartnersTerms) {
      openPartnersTermsAlert();
      return;
    }

    setIsAgreePartnersTerms(false);
  };

  return (
    <Stack
      component="form"
      spacing={20}
      align="center"
      w="100%"
      onSubmit={form.handleSubmit(handleSubmit)}
    >
      <Controller
        control={form.control}
        name="type"
        render={({ field }) => (
          <Input.Wrapper label={t('유형 선택')} size="md" required w="100%">
            <Flex
              gap={8}
              wrap="nowrap"
              align="stretch"
              css={{
                [desktopFirstMediaQuery.mobile]: {
                  flexDirection: 'column',
                },
              }}
            >
              <RadioCardWithoutIndicator
                checked={field.value === PARTNERS_TYPE.PERSONAL}
                imageSrc={`${NEXT_PUBLIC_CDN_URL}/assets/images/partners/child.png`}
                label={t('개인')}
                description={t('홍보 링크를 통해 강의가 판매되면 수익을 받을 수 있어요.')}
                onClick={() => field.onChange(PARTNERS_TYPE.PERSONAL)}
              />

              <RadioCardWithoutIndicator
                checked={field.value === PARTNERS_TYPE.BUSINESS}
                imageSrc={`${NEXT_PUBLIC_CDN_URL}/assets/images/partners/office-building.png`}
                label={t('인플루언서 또는 에이전시(회사)')}
                description={t('별도 계약이 필요해요! 가입 후 연락처로 안내드릴게요.')}
                onClick={() => field.onChange(PARTNERS_TYPE.BUSINESS)}
              />
            </Flex>
          </Input.Wrapper>
        )}
      />

      <TextInput
        size="md"
        label={
          partnersType === PARTNERS_TYPE.PERSONAL ? t('계약자명') : t('인플루언서 또는 에이전시 명')
        }
        required
        placeholder={
          partnersType === PARTNERS_TYPE.PERSONAL
            ? t('실명 입력')
            : t('인플루언서 또는 에이전시 명 입력')
        }
        w="100%"
        error={form.formState.errors.name?.message}
        {...form.register('name')}
      />

      <TextInput
        size="md"
        label={t('이메일')}
        required
        placeholder={t('이메일 입력')}
        w="100%"
        error={form.formState.errors.email?.message}
        {...form.register('email')}
      />

      <Input.Wrapper
        label={t('휴대폰 번호')}
        description={
          partnersType === PARTNERS_TYPE.BUSINESS &&
          t('가입 신청 후 안내받을 번호를 입력해 주세요.')
        }
        required
        w="100%"
        size="md"
        error={
          form.formState.errors.phone?.message && (
            <Text mt={2} size="sm" c="red.5">
              {form.formState.errors.phone?.message}
            </Text>
          )
        }
      >
        <Flex
          gap={8}
          mt={5}
          css={{
            flexDirection: 'row',
            [desktopFirstMediaQuery.mobile]: {
              flexDirection: 'column',
            },
          }}
        >
          <Controller
            control={form.control}
            name="countryCode"
            render={({ field }) => (
              <CountryCodeSelect
                css={{
                  width: 250,
                  [desktopFirstMediaQuery.mobile]: { width: '100%' },
                }}
                lang={lang}
                defaultValue="82"
                size="md"
                mb={0}
                {...field}
                onChange={(value) => {
                  field.onChange(value);
                  form.trigger('phone');
                }}
              />
            )}
          />

          <TextInput
            size="md"
            placeholder={t('휴대폰 번호 입력')}
            w="100%"
            type="tel"
            error={!!form.formState.errors.phone?.message}
            {...form.register('phone')}
          />
        </Flex>
      </Input.Wrapper>

      {partnersType === PARTNERS_TYPE.BUSINESS && (
        <TextInput
          required
          size="md"
          label={t('활동 링크')}
          description={t('회사 URL, 홍보 링크를 게시할 페이지 또는 SNS 링크를 입력해 주세요.')}
          placeholder={t('링크 입력')}
          w="100%"
          error={form.formState.errors.activityLink?.message}
          {...form.register('activityLink')}
        />
      )}

      <CheckboxCard
        size="md"
        label={t('이용약관 동의')}
        description={
          <Text>
            {t('인프런 마케팅 파트너스 활동을 위해서는')}{' '}
            <Anchor
              c="blue.7"
              component={Link}
              href={AUTH_URL.PARTNERS_TERMS()}
              target="_blank"
              onClick={() => trackMixpanelEvent(eventNames.CLICK_PARTNERS_TERMS_LINK)}
            >
              {t('인프런 마케팅 파트너스 약관')}
            </Anchor>
            {t('에 동의하셔야 합니다.')}
          </Text>
        }
        checked={isAgreePartnersTerms}
        required
        onClick={handleClickAgreePartnersTerms}
      >
        {t('마케팅 파트너스 이용약관 동의')}
      </CheckboxCard>

      <Button
        my={16}
        type="submit"
        size="lg"
        w="100%"
        disabled={!form.formState.isValid || !isAgreePartnersTerms || loading}
        loading={loading}
        css={{
          maxWidth: 400,

          [desktopFirstMediaQuery.mobile]: {
            maxWidth: 'unset',
          },
        }}
      >
        {t('가입하기')}
      </Button>
    </Stack>
  );
};
